import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Loader2, Trash2, Star, X, Search } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import MediaUploader from "../../components/admin/MediaUploader";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const sel = "w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1";

const TYPES = [
  ["percentage", "Percentage discount (% off)"], ["fixed", "Fixed £ discount"],
  ["bogof", "Buy One Get One Free"], ["bogo_half", "Buy One Get One Half Price"],
  ["b2g1f", "Buy Two Get One Free"], ["bundle", "Bundle discount"],
  ["launch", "Launch offer"], ["voucher", "Coupon"], ["other", "Other / campaign"],
];

const EMPTY = {
  name: "", internal_name: "", promotion_type: "percentage", short_description: "",
  badge_label: "", desktop_image: "", mobile_image: "", image_focal_point: "center",
  overlay_strength: 60, text_align: "left", cta_label: "Shop Offer", cta_url: "",
  start_at: "", end_at: "", is_active: true, show_on_homepage: true, is_featured: false,
  display_order: 0, linked_product_ids: [], linked_category_ids: [], voucher_code: "",
  terms_summary: "",
  // Phase 5 (inactive) discount-engine fields:
  discount_percent: "", discount_amount: "", min_spend: "", max_discount: "",
  usage_limit_total: "", usage_limit_per_customer: "", voucher_required: false,
  stackable: false, priority: 0, customer_eligibility: "all",
  is_subscriber_campaign: false, voucher_expiry_days: 30, one_per_subscriber: true,
};

const STATUS_STYLE = {
  active: "text-emerald-400", scheduled: "text-amber", expired: "text-firered", inactive: "text-white/40",
};

const dtLocal = (s) => (s ? String(s).slice(0, 16) : "");

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("all");
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [prodQuery, setProdQuery] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/promotions").then(({ data }) => setPromos(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/admin/products", { params: { limit: 500 } }).then(({ data }) => setProducts(data.items || [])).catch(() => {});
  }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setProdQuery(""); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, start_at: dtLocal(p.start_at), end_at: dtLocal(p.end_at),
      discount_percent: p.discount_percent ?? "", discount_amount: p.discount_amount ?? "",
      min_spend: p.min_spend ?? "", max_discount: p.max_discount ?? "",
      usage_limit_total: p.usage_limit_total ?? "", usage_limit_per_customer: p.usage_limit_per_customer ?? "" });
    setEditingId(p.promotion_id); setProdQuery(""); setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toNum = (v) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Promotion name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      display_order: parseInt(form.display_order) || 0,
      overlay_strength: parseInt(form.overlay_strength) || 0,
      priority: parseInt(form.priority) || 0,
      discount_percent: toNum(form.discount_percent), discount_amount: toNum(form.discount_amount),
      min_spend: toNum(form.min_spend), max_discount: toNum(form.max_discount),
      usage_limit_total: toNum(form.usage_limit_total), usage_limit_per_customer: toNum(form.usage_limit_per_customer),
      voucher_expiry_days: toNum(form.voucher_expiry_days) || 30,
      cta_url: form.cta_url?.trim() || null,
    };
    try {
      if (editingId) await api.patch(`/admin/promotions/${editingId}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success(editingId ? "Promotion updated" : "Promotion created");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = async (p) => { try { await api.post(`/admin/promotions/${p.promotion_id}/toggle`); load(); } catch { toast.error("Could not update status"); } };
  const remove = async (p) => {
    if (!window.confirm(`Delete promotion "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/promotions/${p.promotion_id}`); toast.success("Promotion deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => tab === "all" ? promos : promos.filter((p) => p.status === tab), [promos, tab]);
  const counts = useMemo(() => promos.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {}), [promos]);

  const toggleCat = (id) => set("linked_category_ids", form.linked_category_ids.includes(id) ? form.linked_category_ids.filter((x) => x !== id) : [...form.linked_category_ids, id]);
  const addProd = (id) => { if (!form.linked_product_ids.includes(id)) set("linked_product_ids", [...form.linked_product_ids, id]); setProdQuery(""); };
  const rmProd = (id) => set("linked_product_ids", form.linked_product_ids.filter((x) => x !== id));
  const prodName = (id) => products.find((p) => p.product_id === id)?.name || id;
  const prodMatches = useMemo(() => {
    if (!prodQuery.trim()) return [];
    const q = prodQuery.toLowerCase();
    return products.filter((p) => (p.name || "").toLowerCase().includes(q) && !form.linked_product_ids.includes(p.product_id)).slice(0, 8);
  }, [prodQuery, products, form.linked_product_ids]);

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Promotions</h1>
          <p className="mt-2 text-muted-foreground">Create campaign offers shown on the homepage. Expired promotions drop off the site automatically but stay here.</p>
        </div>
        <Button data-testid="add-promotion-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Promotion</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10">
          {["all", "active", "scheduled", "expired", "inactive"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`promo-tab-${t}`} className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold capitalize">
              {t}{t !== "all" && counts[t] ? ` (${counts[t]})` : ""}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="promotions-empty">No promotions in this view.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Promotion</TableHead>
                <TableHead className="text-white/70">Type</TableHead>
                <TableHead className="text-white/70">Homepage</TableHead>
                <TableHead className="text-white/70">Order</TableHead>
                <TableHead className="text-white/70">Status</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.promotion_id} className="border-white/10" data-testid={`promotion-row-${p.promotion_id}`}>
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-3">
                      {p.desktop_image && <img src={p.desktop_image} alt="" className="h-9 w-14 rounded object-cover" />}
                      <div>
                        <div className="flex items-center gap-1.5">{p.is_featured && <Star className="h-3.5 w-3.5 text-gold fill-gold" />}{p.name}</div>
                        {p.badge_label && <div className="text-[11px] text-gold/80 uppercase tracking-wide">{p.badge_label}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{p.promotion_type}</TableCell>
                  <TableCell className="text-sm">{p.show_on_homepage ? <span className="text-emerald-400">Shown</span> : <span className="text-white/40">Hidden</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{p.display_order}</TableCell>
                  <TableCell><span className={`text-sm capitalize ${STATUS_STYLE[p.status] || "text-white/60"}`} data-testid={`promo-status-${p.promotion_id}`}>{p.status}</span></TableCell>
                  <TableCell className="text-right space-x-1">
                    <button onClick={() => toggle(p)} title="Toggle active" data-testid={`toggle-promotion-${p.promotion_id}`} className={`text-xs px-2 py-1 rounded ${p.is_active ? "text-emerald-400" : "text-firered"}`}>{p.is_active ? "Active" : "Inactive"}</button>
                    <button onClick={() => openEdit(p)} title="Edit" data-testid={`edit-promotion-${p.promotion_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p)} title="Delete" data-testid={`delete-promotion-${p.promotion_id}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-white/10"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="promotion-dialog">
          <DialogHeader><DialogTitle className="font-playfair text-xl">{editingId ? "Edit Promotion" : "New Promotion"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Promotion name *</Label><Input data-testid="promo-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Internal name (admin only)</Label><Input value={form.internal_name || ""} onChange={(e) => set("internal_name", e.target.value)} className={inp} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Type</Label>
                <select data-testid="promo-type-select" className={sel} value={form.promotion_type} onChange={(e) => set("promotion_type", e.target.value)}>
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><Label className="text-white/80">Badge / label text</Label><Input data-testid="promo-badge-input" value={form.badge_label || ""} onChange={(e) => set("badge_label", e.target.value)} placeholder="e.g. 20% OFF" className={inp} /></div>
            </div>
            <div><Label className="text-white/80">Short public description</Label><Textarea value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Terms summary (optional)</Label><Input value={form.terms_summary || ""} onChange={(e) => set("terms_summary", e.target.value)} className={inp} /></div>

            <div className="grid sm:grid-cols-2 gap-4 border-t border-white/10 pt-4">
              <div><Label className="text-white/80">Desktop banner image</Label><MediaUploader value={form.desktop_image ? [{ url: form.desktop_image, is_primary: true }] : []} onChange={(m) => set("desktop_image", m[0]?.url || "")} /></div>
              <div><Label className="text-white/80">Mobile image (optional)</Label><MediaUploader value={form.mobile_image ? [{ url: form.mobile_image, is_primary: true }] : []} onChange={(m) => set("mobile_image", m[0]?.url || "")} /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label className="text-white/80">Focal point</Label>
                <select className={sel} value={form.image_focal_point} onChange={(e) => set("image_focal_point", e.target.value)}>
                  <option value="center">Center</option><option value="top">Top</option><option value="bottom">Bottom</option>
                </select>
              </div>
              <div><Label className="text-white/80">Overlay strength ({form.overlay_strength})</Label><Input type="range" min="0" max="100" value={form.overlay_strength} onChange={(e) => set("overlay_strength", e.target.value)} className="mt-3 w-full accent-gold" /></div>
              <div><Label className="text-white/80">Text alignment</Label>
                <select className={sel} value={form.text_align} onChange={(e) => set("text_align", e.target.value)}>
                  <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 border-t border-white/10 pt-4">
              <div><Label className="text-white/80">CTA label</Label><Input value={form.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">CTA URL (blank = auto from targeting)</Label><Input data-testid="promo-cta-url-input" value={form.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="/fireworks?..." className={inp} /></div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Start date/time</Label><Input type="datetime-local" data-testid="promo-start-input" value={form.start_at || ""} onChange={(e) => set("start_at", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">End date/time</Label><Input type="datetime-local" data-testid="promo-end-input" value={form.end_at || ""} onChange={(e) => set("end_at", e.target.value)} className={inp} /></div>
            </div>

            <div className="grid sm:grid-cols-4 gap-4 items-end border-t border-white/10 pt-4">
              <div><Label className="text-white/80">Display order</Label><Input type="number" data-testid="promo-order-input" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className={inp} /></div>
              <div className="flex items-center gap-2"><Switch data-testid="promo-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /><Label className="text-white/80">Active</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="promo-homepage-switch" checked={form.show_on_homepage} onCheckedChange={(v) => set("show_on_homepage", v)} /><Label className="text-white/80">Homepage</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="promo-featured-switch" checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} /><Label className="text-white/80">Featured</Label></div>
            </div>

            {/* Targeting */}
            <div className="border-t border-white/10 pt-4 space-y-3">
              <Label className="text-white/80">Linked categories</Label>
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                  <button type="button" key={c.category_id} onClick={() => toggleCat(c.category_id)}
                    className={`px-3 py-1 rounded-full text-xs border ${form.linked_category_ids.includes(c.category_id) ? "bg-gold/20 border-gold/50 text-gold" : "border-white/15 text-white/60 hover:border-white/30"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
              <Label className="text-white/80">Linked products</Label>
              <div className="relative">
                <div className="flex items-center gap-2 rounded-md bg-black/30 border border-white/15 px-3"><Search className="h-4 w-4 text-white/40" />
                  <input value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder="Search products to link…" className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none" data-testid="promo-product-search" />
                </div>
                {prodMatches.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-white/15 bg-navy shadow-lg max-h-56 overflow-y-auto">
                    {prodMatches.map((p) => (
                      <button type="button" key={p.product_id} onClick={() => addProd(p.product_id)} className="block w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10">{p.name}</button>
                    ))}
                  </div>
                )}
              </div>
              {form.linked_product_ids.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.linked_product_ids.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-white/10 text-white">{prodName(id)}<button type="button" onClick={() => rmProd(id)} className="text-white/50 hover:text-firered"><X className="h-3 w-3" /></button></span>
                  ))}
                </div>
              )}
            </div>

            {/* Phase 5 discount engine — future-ready, inactive */}
            <details className="border-t border-white/10 pt-4">
              <summary className="cursor-pointer text-white/70 text-sm">Discount rules & limits <span className="text-white/40">(Phase 5 — stored but not applied yet)</span></summary>
              <div className="mt-4 grid sm:grid-cols-3 gap-4">
                <div><Label className="text-white/70 text-xs">Discount %</Label><Input type="number" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Discount £</Label><Input type="number" value={form.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Min spend £</Label><Input type="number" value={form.min_spend} onChange={(e) => set("min_spend", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Max discount £</Label><Input type="number" value={form.max_discount} onChange={(e) => set("max_discount", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Voucher code</Label><Input value={form.voucher_code || ""} onChange={(e) => set("voucher_code", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Priority</Label><Input type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Usage limit (total)</Label><Input type="number" value={form.usage_limit_total} onChange={(e) => set("usage_limit_total", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Usage limit / customer</Label><Input type="number" value={form.usage_limit_per_customer} onChange={(e) => set("usage_limit_per_customer", e.target.value)} className={inp} /></div>
                <div><Label className="text-white/70 text-xs">Customer eligibility</Label>
                  <select className={sel} value={form.customer_eligibility} onChange={(e) => set("customer_eligibility", e.target.value)}>
                    <option value="all">All customers</option><option value="new">New customers</option><option value="returning">Returning</option><option value="vip">VIP list</option>
                  </select>
                </div>
                <div className="flex items-center gap-2"><Switch checked={form.voucher_required} onCheckedChange={(v) => set("voucher_required", v)} /><Label className="text-white/70 text-xs">Coupon required</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.stackable} onCheckedChange={(v) => set("stackable", v)} /><Label className="text-white/70 text-xs">Stackable</Label></div>
                <div className="col-span-2 mt-2 rounded-lg border border-gold/20 bg-gold/5 p-3 space-y-3" data-testid="subscriber-campaign-block">
                  <div className="flex items-center gap-2"><Switch data-testid="promo-subscriber-toggle" checked={form.is_subscriber_campaign} onCheckedChange={(v) => set("is_subscriber_campaign", v)} /><Label className="text-gold text-xs font-medium">Mailing-list incentive campaign (issues a unique coupon to each new subscriber)</Label></div>
                  {form.is_subscriber_campaign && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-white/70 text-xs">Coupon expiry (days)</Label><Input type="number" data-testid="promo-voucher-expiry" value={form.voucher_expiry_days} onChange={(e) => set("voucher_expiry_days", e.target.value)} className={inp} /></div>
                      <div className="flex items-center gap-2 pt-5"><Switch checked={form.one_per_subscriber} onCheckedChange={(v) => set("one_per_subscriber", v)} /><Label className="text-white/70 text-xs">One coupon per subscriber</Label></div>
                    </div>
                  )}
                </div>
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-promotion-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Promotion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
